import { useStore } from '@/contexts/StoreContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

/**
 * Como a imagem da skin deve ser enquadrada.
 *
 * - `busto` (Minecraft): render chapado de cabeça, tronco e braços, em pixel
 *   art. Fica encostado na base do cartão e sem suavização (`pixelated`).
 * - `corpo` (Hytale): render 3D de corpo inteiro com fundo transparente.
 *   Fica inteiro (`contain`) e com suavização normal.
 */
export type FormatoDaSkin = 'busto' | 'corpo';

export interface Skin {
  url: string;
  formato: FormatoDaSkin;
}

/**
 * Returns the correct avatar URL based on the store's game type.
 * - MINECRAFT: internal API → /api/v1/minecraft/avatar/:nick?size=N
 * - HYTALE (default): https://hyvatar.io/render/:nick?size=N
 */
export function useAvatar() {
  const { store } = useStore();
  const isMinecraft = store?.gameType === 'MINECRAFT';
  // Loja de produtos digitais não tem jogo: sem skin, fica o boneco neutro ou a inicial.
  const semJogo = store?.gameType === 'DIGITAL';

  function getAvatarUrl(nick: string | null | undefined, size: number = 128): string {
    const safenick = nick?.trim() || '';
    if (!safenick || semJogo) return '';

    if (isMinecraft) {
      return `${API_BASE}/minecraft/avatar/${encodeURIComponent(safenick)}?size=${size}`;
    }

    return `https://hyvatar.io/render/${encodeURIComponent(safenick)}?size=${size}`;
  }

  function getFullBodyUrl(nick: string | null | undefined, size: number = 512): string {
    const safenick = nick?.trim() || '';
    if (!safenick || semJogo) return '';

    if (isMinecraft) {
      return `${API_BASE}/minecraft/avatar/${encodeURIComponent(safenick)}?size=${size}`;
    }

    return `https://hyvatar.io/render/full/${encodeURIComponent(safenick)}?size=${size}`;
  }

  /**
   * A skin para vitrine (ranking, destaque): busto no Minecraft, corpo
   * inteiro no Hytale. Cada jogo tem um render diferente, e o `formato` diz
   * ao componente como enquadrar (ver `FormatoDaSkin`).
   */
  function getSkin(nick: string | null | undefined, size: number = 256): Skin | null {
    const safenick = nick?.trim() || '';
    if (!safenick || semJogo) return null;

    if (isMinecraft) {
      return {
        url: `${API_BASE}/minecraft/avatar/${encodeURIComponent(safenick)}?size=${size}&shape=bust`,
        formato: 'busto',
      };
    }

    return { url: `https://hyvatar.io/render/full/${encodeURIComponent(safenick)}?size=${size}`, formato: 'corpo' };
  }

  return { getAvatarUrl, getFullBodyUrl, getSkin, isMinecraft };
}
